import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  Image,
} from 'react-native';
import { useMutation } from '@tanstack/react-query';
import AnimatedPressable from '@/components/AnimatedPressable';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { Mail, ChevronLeft, Send, CheckCircle } from 'lucide-react-native';
import Animated, { FadeInDown, FadeInUp } from 'react-native-reanimated';
import { useSystemThemeColors } from '@/lib/useThemeColors';
import { useAlert } from '@/components/AlertDialog';
import { AuthLayout } from '@/components/AuthLayout';
import { isWeb } from '@/lib/platform';
import { useAuth, isSupabaseConfigured } from '@/lib/supabase';
import { getUserMessage } from '@/lib/errors';

import { LOGO_MARK } from '@/lib/logo';

type ScreenState = 'input' | 'sent';

export default function ForgotPasswordScreen() {
  const router = useRouter();
  const colors = useSystemThemeColors();
  const { alert, error: showError } = useAlert();
  const { resetPassword, isConfigured } = useAuth();

  const [email, setEmail] = useState('');
  const [screenState, setScreenState] = useState<ScreenState>('input');

  const resetMutation = useMutation({
    mutationFn: async () => {
      if (isConfigured && isSupabaseConfigured()) {
        const { error } = await resetPassword(email);
        if (error) throw error;
      } else {
        await new Promise(resolve => setTimeout(resolve, 1500));
      }
    },
    onSuccess: () => setScreenState('sent'),
    onError: (error: any) => showError('Reset Failed', getUserMessage(error)),
  });

  const isLoading = resetMutation.isPending;

  const handleResetPassword = () => {
    if (!email.trim()) {
      alert('Missing Information', 'Please enter your email address.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      alert('Invalid Email', 'Please enter a valid email address.');
      return;
    }

    resetMutation.mutate();
  };

  const handleBackToLogin = () => {
    router.back();
  };

  return (
    <AuthLayout>
      <View className="flex-1" style={{ backgroundColor: isWeb ? colors.surface.primary : colors.surface.secondary }}>
        <SafeAreaView edges={isWeb ? [] : ['top', 'bottom']} className="flex-1">
          <KeyboardAvoidingView
            behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
            className="flex-1"
          >
            <ScrollView
              className="flex-1"
              showsVerticalScrollIndicator={false}
              contentContainerStyle={{ flexGrow: 1, justifyContent: isWeb ? 'center' : 'flex-start' }}
              keyboardShouldPersistTaps="handled"
            >
              {/* Header - hide on web */}
              {!isWeb && (
                <Animated.View
                  entering={FadeInDown.delay(100).duration(500)}
                  className="px-5 pt-4"
                >
                  <AnimatedPressable
                    onPress={() => router.back()}
                    className="w-10 h-10 rounded-full items-center justify-center"
                    style={{ backgroundColor: colors.surface.primary }}
                  >
                    <ChevronLeft size={24} color={colors.text.primary} />
                  </AnimatedPressable>
                </Animated.View>
              )}

              {screenState === 'input' ? (
                <>
                  {/* Logo & Title */}
                  <Animated.View
                    entering={FadeInDown.delay(200).duration(500)}
                    className={`items-center px-5 ${isWeb ? 'pt-4 pb-6' : 'pt-8 pb-8'}`}
                  >
                    {!isWeb && (
                      <Image
                        source={LOGO_MARK}
                        className="w-20 h-20 mb-6"
                        resizeMode="contain"
                      />
                    )}
                    <Text style={{ color: colors.text.primary }} className="text-2xl font-bold mb-2">
                      Reset Password
                    </Text>
                    <Text style={{ color: colors.text.secondary }} className="text-center text-base leading-relaxed px-4">
                      Enter your email address and we'll send you a link to reset your password.
                    </Text>
                  </Animated.View>

                  {/* Email Input */}
                  <Animated.View
                    entering={FadeInUp.delay(300).duration(500)}
                    className="px-5"
                  >
                    <Text style={{ color: colors.text.secondary }} className="text-sm mb-2 font-medium">
                      Email Address
                    </Text>
                    <View
                      className="flex-row items-center rounded-xl px-4"
                      style={{ backgroundColor: colors.surface.primary, borderWidth: 1, borderColor: colors.border.light }}
                    >
                      <Mail size={20} color={colors.text.tertiary} />
                      <TextInput
                        style={{ color: colors.text.primary }}
                        className="flex-1 py-4 pl-3 text-base"
                        placeholder="Enter your email"
                        placeholderTextColor={colors.text.tertiary}
                        value={email}
                        onChangeText={setEmail}
                        keyboardType="email-address"
                        autoCapitalize="none"
                        autoComplete="email"
                        autoFocus
                      />
                    </View>
                  </Animated.View>

                  {/* Submit Button */}
                  <Animated.View
                    entering={FadeInUp.delay(400).duration(500)}
                    className="px-5 mt-8"
                  >
                    <AnimatedPressable
                      onPress={handleResetPassword}
                      disabled={isLoading}
                      className="rounded-xl py-4 flex-row items-center justify-center gap-2"
                      style={{
                        backgroundColor: isLoading ? colors.border.medium : colors.primary[600],
                      }}
                    >
                      <Text style={{ color: colors.text.inverse }} className="font-semibold text-base">
                        {isLoading ? 'Sending...' : 'Send Reset Link'}
                      </Text>
                      {!isLoading && <Send size={20} color={colors.text.inverse} />}
                    </AnimatedPressable>
                  </Animated.View>

                  {/* Back to Login */}
                  <Animated.View
                    entering={FadeInUp.delay(500).duration(500)}
                    className="flex-row items-center justify-center px-5 mt-6 pb-8"
                  >
                    <Text style={{ color: colors.text.secondary }} className="text-sm">
                      Remember your password?{' '}
                    </Text>
                    <AnimatedPressable onPress={handleBackToLogin}>
                      <Text style={{ color: colors.primary[600] }} className="text-sm font-semibold">
                        Sign In
                      </Text>
                    </AnimatedPressable>
                  </Animated.View>
                </>
              ) : (
                <>
                  {/* Success State */}
                  <Animated.View
                    entering={FadeInDown.delay(100).duration(600)}
                    className={`items-center px-5 ${isWeb ? 'pt-4 pb-6' : 'pt-12 pb-8'}`}
                  >
                    <View
                      className="w-24 h-24 rounded-full items-center justify-center mb-6"
                      style={{ backgroundColor: colors.success[50] }}
                    >
                      <CheckCircle size={48} color={colors.success[600]} />
                    </View>
                    <Text style={{ color: colors.text.primary }} className="text-2xl font-bold mb-3">
                      Check Your Email
                    </Text>
                    <Text style={{ color: colors.text.secondary }} className="text-center text-base leading-relaxed px-4 mb-2">
                      We've sent a password reset link to:
                    </Text>
                    <Text style={{ color: colors.primary[600] }} className="font-semibold text-base">
                      {email}
                    </Text>
                  </Animated.View>

                  <Animated.View
                    entering={FadeInUp.delay(200).duration(500)}
                    className="px-5"
                  >
                    <View
                      className="rounded-xl p-4"
                      style={{ backgroundColor: colors.surface.primary, borderWidth: 1, borderColor: colors.border.light }}
                    >
                      <Text style={{ color: colors.text.secondary }} className="text-sm leading-relaxed text-center">
                        Didn't receive the email? Check your spam folder or make sure you entered the correct email address.
                      </Text>
                    </View>
                  </Animated.View>

                  <Animated.View
                    entering={FadeInUp.delay(300).duration(500)}
                    className="px-5 mt-8"
                  >
                    <AnimatedPressable
                      onPress={handleBackToLogin}
                      className="rounded-xl py-4 flex-row items-center justify-center gap-2"
                      style={{ backgroundColor: colors.primary[600] }}
                    >
                      <ChevronLeft size={20} color={colors.text.inverse} />
                      <Text style={{ color: colors.text.inverse }} className="font-semibold text-base">
                        Back to Sign In
                      </Text>
                    </AnimatedPressable>
                  </Animated.View>

                  <Animated.View
                    entering={FadeInUp.delay(400).duration(500)}
                    className="items-center px-5 mt-6 pb-8"
                  >
                    <AnimatedPressable onPress={() => setScreenState('input')}>
                      <Text style={{ color: colors.primary[600] }} className="text-sm font-medium">
                        Try a different email
                      </Text>
                    </AnimatedPressable>
                  </Animated.View>
                </>
              )}
            </ScrollView>
          </KeyboardAvoidingView>
        </SafeAreaView>
      </View>
    </AuthLayout>
  );
}
